import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Plus, Pencil, Trash2, KeyRound, Search, Paperclip, Download, X,
  AlertTriangle, Clock, CheckCircle2, Building2, Upload, ShoppingCart, History,
  RefreshCw, FileClock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { LicenceSpendChart } from "@/components/LicenceSpendChart";
import { LicenceBudgetAlert } from "@/components/LicenceBudget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const emptyForm = {
  product_name: "",
  vendor: "",
  licence_key: "",
  purchase_date: "",
  expiry_date: "",
  seats_total: 1,
  seats_used: 0,
  cost: 0,
  currency: "USD",
  client_id: "",
  notes: "",
};

const STATE_STYLE = {
  active: { cls: "bg-emerald-50 text-emerald-700 ring-emerald-200", Icon: CheckCircle2, label: "Active" },
  expiring: { cls: "bg-amber-50 text-amber-800 ring-amber-200", Icon: Clock, label: "Expiring" },
  expired: { cls: "bg-red-50 text-red-700 ring-red-200", Icon: AlertTriangle, label: "Expired" },
  no_expiry: { cls: "bg-slate-50 text-slate-600 ring-slate-200", Icon: KeyRound, label: "No expiry" },
};

function StateBadge({ state, days }) {
  const s = STATE_STYLE[state] || STATE_STYLE.no_expiry;
  const Icon = s.Icon;
  const suffix =
    state === "expired" && days != null ? ` · ${Math.abs(days)}d ago`
    : state === "expiring" && days != null ? ` · in ${days}d`
    : state === "active" && days != null ? ` · in ${days}d`
    : "";
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ring-1 ring-inset ${s.cls}`}
    >
      <Icon className="size-3" />
      {s.label}
      {suffix}
    </span>
  );
}

function exportLicencesCSV(items) {
  if (!items.length) return;
  const headers = ["Product", "Vendor", "Licence key", "Client",
    "Seats used", "Seats total", "Cost", "Currency",
    "Purchase date", "Expiry date", "Days to expiry", "Status", "Notes"];
  const esc = (v) => {
    const s = String(v ?? "").replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const lines = [headers.join(","), ...items.map((r) => [
    r.product_name, r.vendor, r.licence_key, r.client_snapshot?.name || "",
    r.seats_used ?? 0, r.seats_total ?? 0, r.cost ?? 0, r.currency || "USD",
    r.purchase_date || "", r.expiry_date || "",
    r.days_until_expiry ?? "", (r.expiry_state || "").replace("_", " "), r.notes || "",
  ].map(esc).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const el = document.createElement("a");
  el.href = url; el.download = "software-licences.csv";
  document.body.appendChild(el); el.click(); el.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export default function SoftwareLicences() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [attachOpen, setAttachOpen] = useState(null);

  const params = {};
  if (q) params.q = q;
  if (statusFilter !== "all") params.status = statusFilter;

  const { data, isLoading } = useQuery({
    queryKey: ["licences", q, statusFilter],
    queryFn: () => api.listLicences(params),
  });
  const { data: clients } = useQuery({
    queryKey: ["clients-lite"],
    queryFn: () => api.listClients(),
  });
  const clientOptions = useMemo(
    () => (clients?.items || clients || []).map((c) => ({ id: c.id, name: c.name })),
    [clients],
  );

  const saveMut = useMutation({
    mutationFn: (body) =>
      editing
        ? api.updateLicence(editing.id, body)
        : api.createLicence(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["licences"] });
      qc.invalidateQueries({ queryKey: ["licence-summary"] });
      qc.invalidateQueries({ queryKey: ["licence-spend"] });
      toast.success(editing ? "Licence updated" : "Licence added");
      setDialogOpen(false);
      setEditing(null);
      setForm(emptyForm);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not save licence"),
  });

  const createPoMut = useMutation({
    mutationFn: (id) => api.createLicencePo(id),
    onSuccess: (po) => {
      toast.success(`Draft PO ${po.po_number} created for ${po.supplier_snapshot?.name || "vendor"}`, {
        action: { label: "View POs", onClick: () => navigate("/purchase-orders") },
        duration: 8000,
      });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not create purchase order"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.deleteLicence(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["licences"] });
      qc.invalidateQueries({ queryKey: ["licence-summary"] });
      qc.invalidateQueries({ queryKey: ["licence-spend"] });
      toast.success("Licence deleted");
      setDeleteId(null);
    },
  });

  const items = data?.items || [];
  const counts = useMemo(() => {
    const c = { total: items.length, expired: 0, expiring: 0, active: 0 };
    items.forEach((r) => {
      if (r.expiry_state === "expired") c.expired += 1;
      else if (r.expiry_state === "expiring") c.expiring += 1;
      else if (r.expiry_state === "active") c.active += 1;
    });
    return c;
  }, [items]);

  function openCreate() {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  }
  function openEdit(row) {
    setEditing(row);
    setForm({
      product_name: row.product_name || "",
      vendor: row.vendor || "",
      licence_key: row.licence_key || "",
      purchase_date: row.purchase_date || "",
      expiry_date: row.expiry_date || "",
      seats_total: row.seats_total ?? 1,
      seats_used: row.seats_used ?? 0,
      cost: row.cost ?? 0,
      currency: row.currency || "USD",
      client_id: row.client_id || "",
      notes: row.notes || "",
    });
    setDialogOpen(true);
  }
  function submit(e) {
    e.preventDefault();
    saveMut.mutate({
      ...form,
      cost: Number(form.cost || 0),
      seats_total: Number(form.seats_total || 0),
      seats_used: Number(form.seats_used || 0),
      client_id: form.client_id || null,
    });
  }

  return (
    <div data-testid="software-licences-page" className="space-y-6">
      <PageHeader
        title="Software Licences"
        subtitle="Track licence keys, seats, expiry dates, and set up renewal nudges."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="licences-csv-btn"
              variant="outline"
              onClick={() => exportLicencesCSV(items)}
            >
              <Download className="size-4 mr-1.5" /> CSV
            </Button>
            <Button
              data-testid="new-licence-btn"
              onClick={openCreate}
              className="bg-primary hover:bg-primary/90"
            >
              <Plus className="size-4 mr-1.5" /> New licence
            </Button>
          </div>
        }
      />

      {/* Stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { key: "total", label: "Total", count: counts.total, tone: "bg-slate-50 text-slate-700" },
          { key: "active", label: "Active", count: counts.active, tone: "bg-emerald-50 text-emerald-700" },
          { key: "expiring", label: "Expiring ≤30d", count: counts.expiring, tone: "bg-amber-50 text-amber-800" },
          { key: "expired", label: "Expired", count: counts.expired, tone: "bg-red-50 text-red-700" },
        ].map((s) => (
          <div
            key={s.key}
            data-testid={`licence-stat-${s.key}`}
            className={`rounded-md border border-border p-3 ${s.tone}`}
          >
            <p className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{s.label}</p>
            <p className="mt-0.5 text-2xl font-bold mono">{isLoading ? "—" : s.count}</p>
          </div>
        ))}
      </div>

      <LicenceBudgetAlert variant="banner" />

      <LicenceSpendChart />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            data-testid="licence-search"
            placeholder="Search product, vendor, key…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-8 h-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="licence-status-filter" className="h-9 w-44">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expiring">Expiring soon</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="no_expiry">No expiry</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          data-testid="licences-empty"
          Icon={KeyRound}
          title="No licences yet"
          description="Track every software licence in one place so nothing lapses without warning."
          action={
            <Button onClick={openCreate} className="bg-primary hover:bg-primary/90">
              <Plus className="size-4 mr-1.5" /> Add first licence
            </Button>
          }
        />
      ) : (
        <div className="border border-border rounded-md bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">Product</TableHead>
                <TableHead className="overline">Vendor</TableHead>
                <TableHead className="overline">Expiry</TableHead>
                <TableHead className="overline text-right">Seats</TableHead>
                <TableHead className="overline text-right">Cost</TableHead>
                <TableHead className="overline">Assigned to</TableHead>
                <TableHead className="overline">Status</TableHead>
                <TableHead className="overline text-right w-32">&nbsp;</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow
                  key={r.id}
                  data-testid={`licence-row-${r.id}`}
                  className="hover:bg-slate-50"
                >
                  <TableCell>
                    <div className="font-medium">{r.product_name}</div>
                    {r.licence_key && (
                      <div className="mono text-[11px] text-muted-foreground truncate max-w-[220px]">
                        {r.licence_key}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{r.vendor || "—"}</TableCell>
                  <TableCell className="mono text-xs">{r.expiry_date || "—"}</TableCell>
                  <TableCell className="text-right mono text-sm">
                    {r.seats_used}/{r.seats_total}
                  </TableCell>
                  <TableCell className="text-right mono text-sm">
                    {r.cost > 0
                      ? `${Number(r.cost).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${r.currency}`
                      : "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.client_snapshot?.name || (
                      <span className="text-muted-foreground italic">Internal</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <StateBadge state={r.expiry_state} days={r.days_until_expiry} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button
                        data-testid={`licence-history-btn-${r.id}`}
                        variant="ghost"
                        size="icon"
                        title="Renewal history"
                        onClick={() => setHistoryFor(r)}
                      >
                        <History className="size-4 text-muted-foreground" />
                      </Button>
                      {r.vendor && r.cost > 0 && (
                        <Button
                          data-testid={`licence-create-po-btn-${r.id}`}
                          variant="ghost"
                          size="icon"
                          title={`Create renewal PO — ${r.vendor}, ${Number(r.cost).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${r.currency}`}
                          disabled={createPoMut.isPending}
                          onClick={() => createPoMut.mutate(r.id)}
                        >
                          <ShoppingCart className="size-4 text-emerald-700" />
                        </Button>
                      )}
                      <Button
                        data-testid={`licence-attach-btn-${r.id}`}
                        variant="ghost"
                        size="icon"
                        title={r.has_attachment ? "View / update attachment" : "Attach licence file"}
                        onClick={() => setAttachOpen(r)}
                      >
                        <Paperclip
                          className={`size-4 ${r.has_attachment ? "text-primary" : "text-muted-foreground"}`}
                        />
                      </Button>
                      <Button
                        data-testid={`licence-edit-btn-${r.id}`}
                        variant="ghost"
                        size="icon"
                        onClick={() => openEdit(r)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        data-testid={`licence-delete-btn-${r.id}`}
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setDeleteId(r.id)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="licence-dialog"
          className="sm:max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle>{editing ? "Edit licence" : "New licence"}</DialogTitle>
            <DialogDescription>
              Renewing the expiry date resets the nudge history so the nightly
              cron will announce the new term again.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="overline">Product name *</Label>
              <Input
                data-testid="licence-input-product"
                value={form.product_name}
                onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label className="overline">Vendor</Label>
              <Input
                data-testid="licence-input-vendor"
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                placeholder="Adobe, Microsoft…"
              />
            </div>
            <div>
              <Label className="overline">Licence key</Label>
              <Input
                data-testid="licence-input-key"
                value={form.licence_key}
                onChange={(e) => setForm({ ...form, licence_key: e.target.value })}
                className="mono text-xs"
              />
            </div>
            <div>
              <Label className="overline">Purchase date</Label>
              <Input
                data-testid="licence-input-purchase"
                type="date"
                value={form.purchase_date}
                onChange={(e) => setForm({ ...form, purchase_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">Expiry date</Label>
              <Input
                data-testid="licence-input-expiry"
                type="date"
                value={form.expiry_date}
                onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">Seats total</Label>
              <Input
                data-testid="licence-input-seats-total"
                type="number"
                min="0"
                value={form.seats_total}
                onChange={(e) => setForm({ ...form, seats_total: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">Seats used</Label>
              <Input
                data-testid="licence-input-seats-used"
                type="number"
                min="0"
                value={form.seats_used}
                onChange={(e) => setForm({ ...form, seats_used: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">Cost</Label>
              <Input
                data-testid="licence-input-cost"
                type="number"
                step="0.01"
                min="0"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
              />
            </div>
            <div>
              <Label className="overline">Currency</Label>
              <Input
                data-testid="licence-input-currency"
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                maxLength={3}
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">Assign to client (optional)</Label>
              <Select
                value={form.client_id || "__internal__"}
                onValueChange={(v) => setForm({ ...form, client_id: v === "__internal__" ? "" : v })}
              >
                <SelectTrigger data-testid="licence-input-client">
                  <SelectValue placeholder="Internal use" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__internal__">Internal use only</SelectItem>
                  {clientOptions.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2">
                        <Building2 className="size-3.5" />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="overline">Notes</Label>
              <Textarea
                data-testid="licence-input-notes"
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                data-testid="licence-save-btn"
                type="submit"
                disabled={saveMut.isPending}
                className="bg-primary hover:bg-primary/90"
              >
                {saveMut.isPending ? "Saving…" : editing ? "Save changes" : "Create licence"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Attachment dialog */}
      <AttachmentDialog
        row={attachOpen}
        onClose={() => setAttachOpen(null)}
        onChanged={() => {
          qc.invalidateQueries({ queryKey: ["licences"] });
        }}
      />

      <LicenceHistoryDialog licence={historyFor} onClose={() => setHistoryFor(null)} />

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent data-testid="licence-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this licence?</AlertDialogTitle>
            <AlertDialogDescription>
              The licence, any attached file, and its renewal nudge history
              will be permanently removed. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="licence-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteMut.mutate(deleteId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AttachmentDialog({ row, onClose, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  async function pickAndUpload(e) {
    const file = e.target.files?.[0];
    if (!file || !row) return;
    setBusy(true);
    try {
      await api.uploadLicenceAttachment(row.id, file);
      toast.success("Attachment uploaded");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }
  async function remove() {
    if (!row) return;
    setBusy(true);
    try {
      await api.deleteLicenceAttachment(row.id);
      toast.success("Attachment removed");
      onChanged?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="licence-attachment-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{row?.has_attachment ? "Licence file" : "Attach licence file"}</DialogTitle>
          <DialogDescription>
            {row?.has_attachment
              ? "Download, replace, or remove the licence document attached to this record."
              : "Upload a PDF or image (max 8 MB) so the licence certificate lives with the record."}
          </DialogDescription>
        </DialogHeader>
        {row?.has_attachment && (
          <div className="rounded-md border border-border p-3 bg-slate-50">
            <div className="flex items-center gap-2 text-sm">
              <Paperclip className="size-4 text-primary" />
              <span className="truncate flex-1" data-testid="licence-attachment-name">
                {row.attachment?.name || "licence file"}
              </span>
              <span className="text-xs text-muted-foreground mono">
                {row.attachment?.size
                  ? `${Math.round(row.attachment.size / 1024)} KB`
                  : ""}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                data-testid="licence-attachment-download"
                variant="outline"
                size="sm"
                onClick={() =>
                  api.downloadLicenceAttachment(row.id, row.attachment?.name)
                }
              >
                <Download className="size-3.5 mr-1.5" /> Download
              </Button>
              <Button
                data-testid="licence-attachment-remove"
                variant="outline"
                size="sm"
                className="text-red-600"
                disabled={busy}
                onClick={remove}
              >
                <X className="size-3.5 mr-1.5" /> Remove
              </Button>
            </div>
          </div>
        )}
        <div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,image/*,.doc,.docx,.txt"
            onChange={pickAndUpload}
            className="hidden"
            data-testid="licence-attachment-file-input"
          />
          <Button
            data-testid="licence-attachment-upload"
            variant={row?.has_attachment ? "outline" : "default"}
            className={row?.has_attachment ? "" : "bg-primary hover:bg-primary/90"}
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            <Upload className="size-3.5 mr-1.5" />
            {busy ? "Uploading…" : row?.has_attachment ? "Replace file" : "Choose file to upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


const HISTORY_KINDS = {
  created: { label: "Licence created", Icon: Plus, cls: "text-slate-600 bg-slate-100" },
  updated: { label: "Details updated", Icon: Pencil, cls: "text-sky-700 bg-sky-50" },
  renewed: { label: "Renewed — expiry extended", Icon: RefreshCw, cls: "text-emerald-700 bg-emerald-50" },
  po_created: { label: "Renewal PO raised", Icon: ShoppingCart, cls: "text-amber-700 bg-amber-50" },
  attachment_added: { label: "Attachment added", Icon: Paperclip, cls: "text-slate-600 bg-slate-100" },
  attachment_removed: { label: "Attachment removed", Icon: X, cls: "text-slate-600 bg-slate-100" },
};

function historyMetaLine(ev) {
  const m = ev.meta || {};
  if (ev.kind === "renewed") {
    return [m.old_expiry && `from ${m.old_expiry}`, m.new_expiry && `to ${m.new_expiry}`,
      m.via_po && `via ${m.via_po}`].filter(Boolean).join(" ");
  }
  if (ev.kind === "po_created") {
    return [m.po_number, m.status, m.total != null &&
      `${Number(m.total).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${m.currency || ""}`]
      .filter(Boolean).join(" · ");
  }
  const bits = Object.entries(m)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== "object")
    .slice(0, 3)
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`);
  return bits.join(" · ");
}

function LicenceHistoryDialog({ licence, onClose }) {
  const { data, isLoading } = useQuery({
    queryKey: ["licence-history", licence?.id],
    queryFn: () => api.licenceHistory(licence.id),
    enabled: !!licence,
  });
  const events = data?.events || [];
  return (
    <Dialog open={!!licence} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="licence-history-dialog" className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileClock className="size-4 text-primary" />
            History — {licence?.product_name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Renewals, linked purchase orders and edits, newest first.
          </DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p className="text-sm text-muted-foreground py-4">Loading…</p>
        ) : events.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">No history recorded yet.</p>
        ) : (
          <ul className="relative pl-5 space-y-4 before:absolute before:left-[9px] before:top-1 before:bottom-1 before:w-px before:bg-border" data-testid="licence-history-timeline">
            {events.map((ev, i) => {
              const k = HISTORY_KINDS[ev.kind] || {
                label: ev.kind.replace(/_/g, " "), Icon: Clock, cls: "text-slate-600 bg-slate-100",
              };
              const K = k.Icon;
              const meta = historyMetaLine(ev);
              return (
                <li key={i} className="relative" data-testid={`licence-history-event-${i}`}>
                  <span className={`absolute -left-5 top-0.5 size-[19px] grid place-items-center rounded-full ${k.cls}`}>
                    <K className="size-3" />
                  </span>
                  <p className="text-sm font-medium leading-tight ml-1">{k.label}</p>
                  <p className="text-[11px] text-muted-foreground ml-1 mono">
                    {ev.ts ? new Date(ev.ts).toLocaleString() : "—"}
                    {ev.actor ? ` · ${ev.actor}` : ""}
                  </p>
                  {meta && <p className="text-xs text-muted-foreground ml-1 mt-0.5">{meta}</p>}
                </li>
              );
            })}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

